import { useState, useEffect, useRef } from "react";

// from claude
/**
 * Custom hook to calculate and increment progress percentage based on a task's duration
 *
 * @param {number} totalDurationMs - The total expected duration of the task in milliseconds
 * @param {boolean} isActive - Whether the progress should be actively updating
 * @param {number} updateIntervalMs - How frequently to update the percentage (default: 100ms)
 * @param {number} elapsedDurationMs - Duration already passed in milliseconds (default: 0)
 * @returns {Array} [percentage, resetProgress] - Current percentage value (0-100) and reset function
 */
const useProgressPercentage = (
  totalDurationMs: number,
  isActive: boolean = true,
  updateIntervalMs: number = 100,
  elapsedDurationMs: number = 0
): [number, (newElapsedDurationMs?: number) => void] => {
  const initialPercentage = Math.min(
    100,
    Math.round((elapsedDurationMs / totalDurationMs) * 100)
  );

  const [percentage, setPercentage] = useState(initialPercentage);
  const startTimeRef = useRef<number>(0);
  const elapsedTimeRef = useRef(elapsedDurationMs);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    elapsedTimeRef.current = elapsedDurationMs;
  }, [elapsedDurationMs]);

  // Sync percentage when initialPercentage reaches 100 (state adjustment during render)
  if (initialPercentage >= 100 && percentage < 100) {
    setPercentage(100);
  }

  useEffect(() => {
    if (initialPercentage >= 100) {
      return;
    }
    // Reset and start tracking when activated
    if (isActive && percentage < 100) {
      // Clear any existing interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }

      // Set the start time accounting for already elapsed duration
      startTimeRef.current = Date.now() - elapsedTimeRef.current;

      // Start the interval to update the percentage
      intervalIdRef.current = setInterval(() => {
        const elapsedMs = Date.now() - startTimeRef.current;
        const newPercentage = Math.min(
          100,
          Math.round((elapsedMs / totalDurationMs) * 100)
        );

        setPercentage((prev) => Math.max(prev, newPercentage));

        // Clean up when we reach 100%
        if (newPercentage >= 100) {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
          }
        }
      }, updateIntervalMs);
    } else if (!isActive && intervalIdRef.current) {
      // Pause the progress when inactive
      clearInterval(intervalIdRef.current);
    }

    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [isActive, totalDurationMs, updateIntervalMs, initialPercentage]);

  // Reset function to allow manual resetting of progress with a new elapsed duration
  const resetProgress = (newElapsedDurationMs = 0) => {
    const newPercentage = Math.min(
      100,
      Math.round((newElapsedDurationMs / totalDurationMs) * 100)
    );

    setPercentage(newPercentage);
    elapsedTimeRef.current = newElapsedDurationMs;
    startTimeRef.current = Date.now() - newElapsedDurationMs;
  };

  return [percentage, resetProgress];
};

export default useProgressPercentage;
